/**
 * Dedicated client store for Project Dashboard SignalR-driven UI state.
 * REST queries hydrate snapshots; realtime reducers apply hub events on top.
 */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { MyAssignedProjectContainersResultModel } from 'src/features/companyProjects/navigation/types/MyAssignedProjectContainersModels';
import type { ProjectContainerSectionModel } from 'src/features/companyProjects/projectContainers/dashboard/tabs/home/types/ProjectContainerHomeTypes';
import type {
  ProjectIntakeInboxModel,
  ProjectIntakeSortByPropName
} from 'src/features/companyProjects/projectIntake/inbox/types';
import { applyInboxEventToList } from 'src/features/companyProjects/projectIntake/inbox/hubs/ProjectIntakeInboxHubActions';
import type { ProjectIntakeEvent } from 'src/features/companyProjects/projectIntake/inbox/types';
import { ProjectIntakeEventType } from 'src/features/companyProjects/projectIntake/inbox/types';
import { applyFolderEventToSectionsState } from './projectContainerSectionsFolderEventToState';
import { applyContainerEventToSectionsState } from './projectContainerSectionsIntakeEventToState';
import {
  ProjectSectionIntakeEventType,
  type ProjectContainerEvent,
  type ProjectFolderEvent
} from './projectContainerEventTypes';
import {
  applyProjectContainerListEventToAssignedContainersState,
  type ProjectContainerListEvent
} from './projectContainerEvents';
import {
  applyInboxEventToContainerSectionModels,
  applyInboxEventToSingleSectionModel
} from './projectContainerSectionsInboxEventToState';

export const projectIntakeInboxSortKey = (sortBy?: ProjectIntakeSortByPropName, desc?: boolean): string =>
  `${sortBy ?? ''}:${String(desc ?? false)}`;

export type InboxSnapshot = {
  sortBy?: ProjectIntakeSortByPropName;
  desc?: boolean;
  items: ProjectIntakeInboxModel[];
};

export type ProjectDashboardRealtimeState = {
  inboxByKey: Record<string, InboxSnapshot>;
  assignedContainers: MyAssignedProjectContainersResultModel | undefined;
  homeFeedByContainerId: Record<number, ProjectContainerSectionModel[] | undefined>;
  sectionByCompositeKey: Record<string, ProjectContainerSectionModel | undefined>;
};

type Actions = {
  hydrateInbox: (
    key: string,
    sortBy: ProjectIntakeSortByPropName | undefined,
    desc: boolean | undefined,
    items: ProjectIntakeInboxModel[]
  ) => void;
  hydrateAssignedContainers: (data: MyAssignedProjectContainersResultModel) => void;
  hydrateHomeFeed: (containerId: number, sections: ProjectContainerSectionModel[]) => void;
  hydrateSection: (containerId: number, sectionType: string | number, section: ProjectContainerSectionModel) => void;
  applyInboxEvent: (event: ProjectIntakeEvent) => void;
  applyContainerEvent: (event: ProjectContainerEvent) => void;
  applyFolderEvent: (event: ProjectFolderEvent) => void;
  applyProjectContainerListEvent: (event: ProjectContainerListEvent, currentUserId: string | undefined) => void;
  reset: () => void;
};

const initialState: ProjectDashboardRealtimeState = {
  inboxByKey: {},
  assignedContainers: undefined,
  homeFeedByContainerId: {},
  sectionByCompositeKey: {}
};

export const sectionCompositeKey = (containerId: number, sectionType: string | number): string =>
  `${containerId}:${String(sectionType)}`;

export const useProjectDashboardRealtimeStore = create<ProjectDashboardRealtimeState & Actions>()(
  immer((set, _get) => ({
    ...initialState,

    hydrateInbox: (key, sortBy, desc, items) => {
      set((draft) => {
        draft.inboxByKey[key] = { sortBy, desc, items };
      });
    },

    hydrateAssignedContainers: (data) => {
      set((draft) => {
        draft.assignedContainers = data;
      });
    },

    hydrateHomeFeed: (containerId, sections) => {
      set((draft) => {
        draft.homeFeedByContainerId[containerId] = sections;
      });
    },

    hydrateSection: (containerId, sectionType, section) => {
      set((draft) => {
        draft.sectionByCompositeKey[sectionCompositeKey(containerId, sectionType)] = section;
      });
    },

    applyInboxEvent: (event) => {
      set((draft) => {
        for (const key of Object.keys(draft.inboxByKey)) {
          const snap = draft.inboxByKey[key];
          snap.items = applyInboxEventToList(snap.items, event, snap.sortBy, snap.desc);
        }

        // Folder "New" tags and section lists use home-feed / section snapshots — keep them
        // aligned when another tab marks read (OnInboxEvent only touches inbox above otherwise).
        for (const cid of Object.keys(draft.homeFeedByContainerId)) {
          const id = Number(cid);
          const cur = draft.homeFeedByContainerId[id];
          if (cur === undefined) continue;
          const next = applyInboxEventToContainerSectionModels(cur, event);
          if (next !== cur) {
            draft.homeFeedByContainerId[id] = next;
          }
        }

        for (const k of Object.keys(draft.sectionByCompositeKey)) {
          const cur = draft.sectionByCompositeKey[k];
          if (!cur) continue;
          const next = applyInboxEventToSingleSectionModel(cur, event);
          if (next !== undefined && next !== cur) {
            draft.sectionByCompositeKey[k] = next;
          }
        }
      });
    },

    applyContainerEvent: (event) => {
      const { containerId } = event;
      set((draft) => {
        const home = draft.homeFeedByContainerId[containerId];
        if (home !== undefined) {
          draft.homeFeedByContainerId[containerId] = applyContainerEventToSectionsState(home, event);
        }

        for (const k of Object.keys(draft.sectionByCompositeKey)) {
          if (!k.startsWith(`${containerId}:`)) continue;
          const currentSection = draft.sectionByCompositeKey[k];
          if (!currentSection) continue;
          const updatedList = applyContainerEventToSectionsState([currentSection], event);
          draft.sectionByCompositeKey[k] = updatedList && updatedList.length > 0 ? updatedList[0] : currentSection;
        }
      });
    },

    applyFolderEvent: (event) => {
      const { containerId } = event;
      set((draft) => {
        const home = draft.homeFeedByContainerId[containerId];
        if (home !== undefined) {
          draft.homeFeedByContainerId[containerId] = applyFolderEventToSectionsState(home, event);
        }
      });
    },

    applyProjectContainerListEvent: (event, currentUserId) => {
      set((draft) => {
        if (draft.assignedContainers !== undefined) {
          draft.assignedContainers = applyProjectContainerListEventToAssignedContainersState(
            draft.assignedContainers,
            event,
            currentUserId
          );
        }
      });
    },

    reset: () => {
      set(initialState);
    }
  }))
);

/** Invalidate triggers for inbox events that affect sidebar counts and detail (used by bridge with React Query). */
export function shouldInvalidateAssignedContainersForInboxEvent(event: ProjectIntakeEvent): boolean {
  return (
    event.type === ProjectIntakeEventType.Created ||
    event.type === ProjectIntakeEventType.Removed ||
    event.type === ProjectIntakeEventType.Read ||
    event.type === ProjectIntakeEventType.Unread ||
    event.type === ProjectIntakeEventType.Updated ||
    event.type === ProjectIntakeEventType.Merged
  );
}

/** Container hub intake events — sidebar UnreadCount is not updated in applyContainerEvent; refetch assigned list. */
export function shouldInvalidateAssignedContainersForContainerEvent(event: ProjectContainerEvent): boolean {
  return (
    event.type === ProjectSectionIntakeEventType.IntakeAdded ||
    event.type === ProjectSectionIntakeEventType.IntakeRemoved ||
    event.type === ProjectSectionIntakeEventType.IntakeMoved ||
    event.type === ProjectSectionIntakeEventType.IntakeUpdated
  );
}
